import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "api/client";

// Creative Tim / Material Dashboard 2 React components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";

import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

const SCREENS_KEY = "solvit_screens";
const PROFILE_KEY = "solvit_profile";
const MEDIA_KEY = "solvit_media";

// Muss identisch zu api/client.js bleiben, aber wir brauchen hier raw fetch für bessere Fehlerdetails
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE_URL) ||
  "https://loopbox.cloud/solvit-api/api";

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "–";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getAuthToken(profile) {
  return (
    profile?.accessToken ||
    localStorage.getItem("solvit_auth_token") ||
    localStorage.getItem("solvit_token") ||
    localStorage.getItem("accessToken") ||
    null
  );
}

function resolveMediaUrl(maybeUrl) {
  if (!maybeUrl) return "";
  if (/^https?:\/\//i.test(maybeUrl)) return maybeUrl;

  const normalized = maybeUrl.startsWith("/") ? maybeUrl : `/${maybeUrl}`;

  if (normalized.startsWith("/solvit-api/")) {
    return `${window.location.origin}${normalized}`;
  }

  return `${window.location.origin}/solvit-api${normalized}`;
}

function pickMediaLabel(m) {
  return (
    m?.name ||
    m?.filename ||
    m?.originalName ||
    m?.title ||
    (m?.id ? `Medium ${String(m.id).slice(-6)}` : "Medium")
  );
}

function pickCustomerNumber(parsedProfile) {
  return (
    parsedProfile?.customerNumber ||
    parsedProfile?.customer?.customerNumber ||
    parsedProfile?.user?.customerNumber ||
    parsedProfile?.user?.customer?.customerNumber ||
    null
  );
}

function pickCustomerId(parsedProfile) {
  return (
    parsedProfile?.customerId ||
    parsedProfile?.customer?.id ||
    parsedProfile?.user?.customerId ||
    parsedProfile?.user?.customer?.id ||
    null
  );
}

function inferTypeFromMedia(media) {
  const mime = media?.mimeType || "";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "image";
}

function extractLoopItems(loopPayload) {
  if (Array.isArray(loopPayload)) return loopPayload;
  if (Array.isArray(loopPayload?.items)) return loopPayload.items;
  if (Array.isArray(loopPayload?.loopItems)) return loopPayload.loopItems;
  return [];
}

function normalizeFolder(value) {
  if (typeof value !== "string") return "";
  return value.trim().replace(/^\/+|\/+$/g, "");
}

function normalizeTags(value) {
  if (Array.isArray(value)) return value.map((t) => String(t || "").trim()).filter(Boolean);
  if (typeof value === "string") {
    return value
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeOnlineFlag(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true" || v === "online" || v === "1") return true;
    if (v === "false" || v === "offline" || v === "0") return false;
  }
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  return null;
}

// GET payload -> editedLoop
function normalizeLoopItems(loopPayload, mediaById) {
  const items = extractLoopItems(loopPayload);
  const sorted = [...items].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));

  return sorted.map((it, idx) => {
    const media = it.media || mediaById.get(it.mediaId);
    const type = inferTypeFromMedia(media);

    return {
      // WICHTIG: backend item id behalten (cm....)
      backendItemId: it.id || null,

      // React key
      id: it.id || `loop-${Date.now()}-${Math.random().toString(16).slice(2)}`,

      mediaId: it.mediaId,
      type,
      position: Number(it.position ?? idx),

      // Nur Images haben duration im Editor
      duration: type === "image" ? Number(it.durationSeconds ?? 10) : undefined,
    };
  });
}

export default function Screens() {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  const [screens, setScreens] = useState([]);
  const [controlDevices, setControlDevices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [customerNumber, setCustomerNumber] = useState(null);
  const [allMedia, setAllMedia] = useState([]);
  const [customerId, setCustomerId] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newLocation, setNewLocation] = useState("");

  const [loopOpen, setLoopOpen] = useState(false);
  const [selectedScreenId, setSelectedScreenId] = useState(null);
  const [editedLoop, setEditedLoop] = useState([]);

  const [selectedMediaId, setSelectedMediaId] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  const [savingLoop, setSavingLoop] = useState(false);
  const [screenSearch, setScreenSearch] = useState("");
  const [screenStatusFilter, setScreenStatusFilter] = useState("");
  const [loopMediaSearch, setLoopMediaSearch] = useState("");
  const [loopFolderFilter, setLoopFolderFilter] = useState("");
  const [loopTagFilter, setLoopTagFilter] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      let parsedProfile = null;
      try {
        const profileRaw = localStorage.getItem(PROFILE_KEY);
        parsedProfile = profileRaw ? safeParse(profileRaw) : null;

        if (!cancelled) {
          setProfile(parsedProfile || null);

          const cn = pickCustomerNumber(parsedProfile);
          if (cn) setCustomerNumber(cn);

          const cid = pickCustomerId(parsedProfile);
          if (cid) setCustomerId(cid);
        }
      } catch (e) {
        console.error("[Screens] Fehler beim Laden des Profils", e);
      }

      try {
        const t = getAuthToken(parsedProfile);
        if (!t) throw new Error("Kein Token vorhanden");

        const [screensRes, mediaRes, devicesRes] = await Promise.all([
          apiFetch("/screens", { token: t }),
          apiFetch("/media", { token: t }),
          apiFetch("/control/devices", { token: t }).catch(() => ({ devices: [] })),
        ]);

        const sList = Array.isArray(screensRes) ? screensRes : screensRes?.screens || [];
        const mList = Array.isArray(mediaRes) ? mediaRes : mediaRes?.media || [];
        const dList = Array.isArray(devicesRes) ? devicesRes : devicesRes?.devices || [];

        if (!cancelled) {
          setScreens(sList);
          setAllMedia(mList);
          setControlDevices(dList);
        }

        try {
          localStorage.setItem(SCREENS_KEY, JSON.stringify(sList));
          localStorage.setItem(MEDIA_KEY, JSON.stringify(mList));
        } catch {}

        return;
      } catch (e) {
        console.error("[Screens] Backend Load failed (Screens/Media)", e);
      }

      try {
        const screensRaw = localStorage.getItem(SCREENS_KEY);
        const parsed = screensRaw ? safeParse(screensRaw) : null;
        if (!cancelled) setScreens(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        console.error("[Screens] Fallback Screens load failed", e);
        if (!cancelled) setScreens([]);
      }
      if (!cancelled) setControlDevices([]);

      try {
        const mediaRaw = localStorage.getItem(MEDIA_KEY);
        const parsed = mediaRaw ? safeParse(mediaRaw) : null;
        if (!cancelled) setAllMedia(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        console.error("[Screens] Fallback Media load failed", e);
        if (!cancelled) setAllMedia([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const isAdmin = String(profile?.role || "").toUpperCase() === "ADMIN";

  const filteredScreens = useMemo(() => {
    if (isAdmin) return screens;

    if (customerNumber) {
      return screens.filter(
        (s) =>
          !s.customerNumber ||
          s.customerNumber === customerNumber ||
          s.customer?.customerNumber === customerNumber
      );
    }

    if (customerId) {
      return screens.filter((s) => !s.customerId || s.customerId === customerId || s.customer?.id === customerId);
    }

    return screens;
  }, [screens, isAdmin, customerNumber, customerId]);

  const availableMedia = useMemo(() => {
    if (isAdmin) return allMedia;

    if (customerNumber) {
      return allMedia.filter(
        (m) =>
          !m.customerNumber ||
          m.customerNumber === customerNumber ||
          m.customer?.customerNumber === customerNumber
      );
    }

    if (customerId) {
      return allMedia.filter((m) => !m.customerId || m.customerId === customerId || m.customer?.id === customerId);
    }

    return allMedia;
  }, [allMedia, isAdmin, customerNumber, customerId]);

  const mediaById = useMemo(() => {
    const map = new Map();
    allMedia.forEach((m) => map.set(m.id, m));
    return map;
  }, [allMedia]);

  const controlDeviceByScreenId = useMemo(() => {
    const map = new Map();
    controlDevices.forEach((d) => {
      if (d?.screenId) map.set(String(d.screenId), d);
    });
    return map;
  }, [controlDevices]);

  const columns = [
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Standort", accessor: "location", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Zuletzt gesehen", accessor: "lastSeen", align: "center" },
    { Header: "Aktionen", accessor: "actions", align: "center" },
  ];

  const visibleScreens = useMemo(() => {
    const q = screenSearch.trim().toLowerCase();
    return filteredScreens.filter((screen) => {
      const liveDevice = controlDeviceByScreenId.get(String(screen.id));
      const liveOnline = normalizeOnlineFlag(liveDevice?.online);
      const screenOnline = normalizeOnlineFlag(screen.status);
      const isOnline = liveOnline ?? screenOnline ?? false;

      if (screenStatusFilter === "online" && !isOnline) return false;
      if (screenStatusFilter === "offline" && isOnline) return false;

      if (!q) return true;
      const haystack = [screen.name, screen.location, liveDevice?.location].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [filteredScreens, screenSearch, screenStatusFilter, controlDeviceByScreenId]);

  const rows = visibleScreens.map((screen) => {
    const liveDevice = controlDeviceByScreenId.get(String(screen.id));
    const liveOnline = normalizeOnlineFlag(liveDevice?.online);
    const screenOnline = normalizeOnlineFlag(screen.status);
    const isOnline = liveOnline ?? screenOnline ?? false;
    const lastSeenAt = liveDevice?.lastHeartbeatAt || screen.lastSeenAt;

    return {
    name: screen.name,
    location: screen.location || liveDevice?.location || "–",
    status: (
      <MDBox display="flex" justifyContent="center">
        <MDBox
          component="span"
          px={1.5}
          py={0.5}
          borderRadius="lg"
          color="white"
          bgColor={isOnline ? "success" : "error"}
          fontSize="0.75rem"
        >
          {isOnline ? "Online" : "Offline"}
        </MDBox>
      </MDBox>
    ),
    lastSeen: lastSeenAt ? new Date(lastSeenAt).toLocaleString() : "–",
    actions: (
      <MDButton variant="outlined" color="info" size="small" onClick={() => handleOpenLoopEditor(screen.id)}>
        Loop bearbeiten
      </MDButton>
    ),
  }});

  const onlineCount = useMemo(
    () =>
      filteredScreens.filter((screen) => {
        const liveDevice = controlDeviceByScreenId.get(String(screen.id));
        const liveOnline = normalizeOnlineFlag(liveDevice?.online);
        const screenOnline = normalizeOnlineFlag(screen.status);
        return (liveOnline ?? screenOnline ?? false) === true;
      }).length,
    [filteredScreens, controlDeviceByScreenId]
  );

  const loopFolders = useMemo(() => {
    const set = new Set();
    availableMedia.forEach((m) => {
      const folder = normalizeFolder(m.folder);
      if (folder) set.add(folder);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [availableMedia]);

  const loopTags = useMemo(() => {
    const set = new Set();
    availableMedia.forEach((m) => normalizeTags(m.tags).forEach((tag) => set.add(tag)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [availableMedia]);

  const filteredAvailableMedia = useMemo(() => {
    const q = loopMediaSearch.trim().toLowerCase();
    return availableMedia
      .filter((m) => {
        const folder = normalizeFolder(m.folder);
        const tags = normalizeTags(m.tags);

        if (loopFolderFilter && folder.toLowerCase() !== loopFolderFilter.toLowerCase()) return false;
        if (loopTagFilter && !tags.some((tag) => tag.toLowerCase() === loopTagFilter.toLowerCase())) return false;

        if (!q) return true;
        const haystack = [pickMediaLabel(m), folder, ...tags].join(" ").toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => {
        const aFolder = normalizeFolder(a.folder);
        const bFolder = normalizeFolder(b.folder);
        if (aFolder !== bFolder) return aFolder.localeCompare(bFolder);
        return pickMediaLabel(a).localeCompare(pickMediaLabel(b));
      });
  }, [availableMedia, loopMediaSearch, loopFolderFilter, loopTagFilter]);

  const handleOpenCreate = () => {
    setNewName("");
    setNewLocation("");
    setCreateOpen(true);
  };
  const handleCloseCreate = () => setCreateOpen(false);

  const handleCreateScreen = () => {
    if (!newName.trim() || !customerNumber) return;
    const newScreen = {
      id: `screen-${Date.now()}`,
      customerNumber,
      name: newName.trim(),
      location: newLocation.trim(),
      status: "offline",
      lastSeenAt: null,
    };
    setScreens((prev) => [...prev, newScreen]);
    setCreateOpen(false);
  };

  const handleOpenLoopEditor = async (screenId) => {
    let loopPayload = null;

    try {
      const t = getAuthToken(profile);
      if (!t) throw new Error("Kein Token vorhanden");

      loopPayload = await apiFetch(`/screens/${screenId}/loop`, { token: t });

      const items = extractLoopItems(loopPayload);
      const mediasFromLoop = items.map((it) => it.media).filter(Boolean);

      if (mediasFromLoop.length) {
        setAllMedia((prev) => {
          const map = new Map(prev.map((m) => [m.id, m]));
          mediasFromLoop.forEach((m) => map.set(m.id, m));
          const merged = Array.from(map.values());
          try {
            localStorage.setItem(MEDIA_KEY, JSON.stringify(merged));
          } catch {}
          return merged;
        });
      }
    } catch (e) {
      console.warn("[Screens] Loop load failed (backend)", e);
      loopPayload = null;
    }

    const normalized = loopPayload ? normalizeLoopItems(loopPayload, mediaById) : [];

    setSelectedScreenId(screenId);
    setEditedLoop(normalized);
    setSelectedMediaId("");
    setDragIndex(null);
    setLoopMediaSearch("");
    setLoopFolderFilter("");
    setLoopTagFilter("");
    setLoopOpen(true);
  };

  const handleCloseLoopEditor = () => {
    setLoopOpen(false);
    setSelectedScreenId(null);
    setEditedLoop([]);
    setSelectedMediaId("");
    setDragIndex(null);
    setLoopMediaSearch("");
    setLoopFolderFilter("");
    setLoopTagFilter("");
  };

  const handleAddSelectedMedia = () => {
    if (!selectedMediaId) return;
    const m = availableMedia.find((x) => x.id === selectedMediaId);
    if (!m) return;

    const type = inferTypeFromMedia(m);

    setEditedLoop((prev) =>
      [...prev, { id: `loop-${Date.now()}-${Math.random().toString(16).slice(2)}`, backendItemId: null, mediaId: m.id, type, duration: type === "image" ? 10 : undefined }]
        .map((it, idx) => ({ ...it, position: idx }))
    );

    setSelectedMediaId("");
  };

  const handleDrop = (targetIndex) => {
    if (dragIndex === null || dragIndex === undefined) return;
    if (targetIndex === dragIndex) return;

    setEditedLoop((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(targetIndex, 0, moved);
      return updated.map((it, idx) => ({ ...it, position: idx }));
    });

    setDragIndex(null);
  };

  const handleDeleteItem = (index) => {
    setEditedLoop((prev) => prev.filter((_, i) => i !== index).map((it, idx) => ({ ...it, position: idx })));
  };

  const handleMoveItem = (index, direction) => {
    setEditedLoop((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const [picked] = next.splice(index, 1);
      next.splice(target, 0, picked);
      return next.map((it, idx) => ({ ...it, position: idx }));
    });
  };

  const handleChangeDuration = (index, value) => {
    setEditedLoop((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, duration: Math.max(1, Number(value) || 1) } : item
      )
    );
  };

  // ✅ Save: durationSeconds nur für Images senden, Videos Feld komplett weglassen.
  // ✅ Zusätzlich: id mitsenden, wenn vorhanden (backendItemId)
  // ✅ Raw fetch, damit du fieldErrors in der Konsole siehst.
  const handleSaveLoop = async () => {
    if (!selectedScreenId) return;

    const t = getAuthToken(profile);
    if (!t) {
      console.error("[Screens] Loop save failed: Kein Token vorhanden");
      return;
    }

    setSavingLoop(true);

    // Build payload exactly for backend
    const items = editedLoop
      .filter((i) => i.mediaId)
      .map((i, idx) => {
        const media = mediaById.get(i.mediaId);
        const type = i.type || inferTypeFromMedia(media);

        const base = {
          // Wenn backend item id existiert -> mitsenden (wichtig für Updates)
          ...(i.backendItemId ? { id: i.backendItemId } : {}),
          mediaId: i.mediaId,
          position: idx,
        };

        // NUR images haben durationSeconds
        if (type === "image") {
          return { ...base, durationSeconds: Number(i.duration ?? 10) };
        }
        return base; // video: kein durationSeconds Feld
      });

    const url = `${API_BASE}/screens/${selectedScreenId}/loop`;
    const body = { items };

    try {
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${t}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        console.error("[Screens] Loop save failed (backend). DETAILS:", data || { status: res.status });
        throw new Error(data?.error || "Validation error");
      }

      // Nach Save: frisch aus Backend laden
      const loopPayload = await apiFetch(`/screens/${selectedScreenId}/loop`, { token: t });
      const normalized = normalizeLoopItems(loopPayload, mediaById);
      setEditedLoop(normalized);

      // optional screens reload
      try {
        const screensRes = await apiFetch("/screens", { token: t });
        const freshScreens = Array.isArray(screensRes) ? screensRes : screensRes?.screens || [];
        setScreens(freshScreens);
        try {
          localStorage.setItem(SCREENS_KEY, JSON.stringify(freshScreens));
        } catch {}
      } catch {}

      handleCloseLoopEditor();
    } catch (e) {
      console.error("[Screens] Loop save failed (backend). DETAILS:", e);
      // Dialog offen lassen, damit du nicht alles verlierst
    } finally {
      setSavingLoop(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={1}>
        <MDBox>
          <MDTypography variant="h4" fontWeight="medium">
            Bildschirme
          </MDTypography>
          <MDTypography variant="button" color="text">
            Verwalte hier deine Displays und die dazugehörigen Medien-Loops.
          </MDTypography>

          {!customerNumber && !isAdmin && (
            <MDTypography variant="caption" color="error">
              Keine Kundennummer gefunden. Bitte stelle sicher, dass sie im Profil gespeichert ist.
            </MDTypography>
          )}

          {isAdmin && (
            <MDTypography variant="caption" color="text">
              Admin-Modus: Es werden alle Bildschirme aller Kunden angezeigt.
            </MDTypography>
          )}
        </MDBox>

        {isAdmin && (
          <MDButton variant="gradient" color="info" startIcon={<Icon>tv</Icon>} onClick={handleOpenCreate} disabled={!customerNumber}>
            Neuen Bildschirm hinzufügen
          </MDButton>
        )}
      </MDBox>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="caption" color="text">
                Bildschirme gesamt
              </MDTypography>
              <MDTypography variant="h4">{filteredScreens.length}</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="caption" color="text">
                Online
              </MDTypography>
              <MDTypography variant="h4" color="success">
                {onlineCount}
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="caption" color="text">
                Offline
              </MDTypography>
              <MDTypography variant="h4" color="error">
                {Math.max(0, filteredScreens.length - onlineCount)}
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Bildschirme suchen"
                value={screenSearch}
                onChange={(e) => setScreenSearch(e.target.value)}
                placeholder="Name oder Standort"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small">search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={screenStatusFilter}
                onChange={(e) => setScreenStatusFilter(e.target.value)}
              >
                <MenuItem value="">Alle</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <MDBox>
        {isCompact ? (
          <Grid container spacing={2}>
            {visibleScreens.map((screen) => {
              const liveDevice = controlDeviceByScreenId.get(String(screen.id));
              const liveOnline = normalizeOnlineFlag(liveDevice?.online);
              const screenOnline = normalizeOnlineFlag(screen.status);
              const isOnline = liveOnline ?? screenOnline ?? false;
              const lastSeenAt = liveDevice?.lastHeartbeatAt || screen.lastSeenAt;

              return (
                <Grid item xs={12} key={screen.id}>
                  <Card>
                    <MDBox p={2}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <MDTypography variant="h6" fontWeight="medium">
                          {screen.name}
                        </MDTypography>
                        <MDBox
                          component="span"
                          px={1.25}
                          py={0.4}
                          borderRadius="lg"
                          color="white"
                          bgColor={isOnline ? "success" : "error"}
                          fontSize="0.75rem"
                        >
                          {isOnline ? "Online" : "Offline"}
                        </MDBox>
                      </MDBox>
                      <MDTypography variant="caption" color="text" display="block">
                        Standort: {screen.location || liveDevice?.location || "–"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                        Zuletzt gesehen: {lastSeenAt ? new Date(lastSeenAt).toLocaleString() : "–"}
                      </MDTypography>
                      <MDButton
                        fullWidth
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => handleOpenLoopEditor(screen.id)}
                      >
                        Loop bearbeiten
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
              );
            })}
            {visibleScreens.length === 0 && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={2}>
                    <MDTypography variant="button" color="text">
                      Keine Bildschirme gefunden.
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            )}
          </Grid>
        ) : (
          <DataTable table={{ columns, rows }} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
        )}
      </MDBox>

      <Footer />

      {isAdmin && (
        <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
          <DialogTitle>Neuen Bildschirm hinzufügen</DialogTitle>
          <DialogContent>
            <MDBox mt={1} mb={2}>
              <MDTypography variant="button" fontWeight="regular" color="text">
                Der Bildschirm wird automatisch dieser Kundennummer zugeordnet:
                <br />
                <strong>{customerNumber || "–"}</strong>
              </MDTypography>
            </MDBox>
            <MDBox mb={2}>
              <MDInput fullWidth label="Bildschirm-Name" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </MDBox>
            <MDBox mb={1}>
              <MDInput fullWidth label="Standort (optional)" value={newLocation} onChange={(e) => setNewLocation(e.target.value)} />
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <MDButton variant="text" color="secondary" onClick={handleCloseCreate}>
              Abbrechen
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={handleCreateScreen} disabled={!newName.trim() || !customerNumber}>
              Speichern
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={loopOpen} onClose={handleCloseLoopEditor} fullWidth maxWidth="md">
        <DialogTitle>Medien-Loop bearbeiten</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" mb={2}>
            Wähle Medien aus deiner Mediathek nach Ordner, Tags oder Suche und ordne sie per Drag & Drop.
          </MDTypography>

          <Grid container spacing={1.5} mb={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Medien suchen"
                value={loopMediaSearch}
                onChange={(e) => setLoopMediaSearch(e.target.value)}
                placeholder="Dateiname, Ordner oder Tag"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small">search</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3.5}>
              <TextField
                fullWidth
                select
                label="Ordner"
                value={loopFolderFilter}
                onChange={(e) => setLoopFolderFilter(e.target.value)}
              >
                <MenuItem value="">Alle</MenuItem>
                {loopFolders.map((folder) => (
                  <MenuItem key={folder} value={folder}>
                    {folder}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3.5}>
              <TextField
                fullWidth
                select
                label="Tag"
                value={loopTagFilter}
                onChange={(e) => setLoopTagFilter(e.target.value)}
              >
                <MenuItem value="">Alle</MenuItem>
                {loopTags.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <MDBox display="flex" gap={1} mb={1.5} flexWrap="wrap">
            {loopFolderFilter && <Chip size="small" label={`Ordner: ${loopFolderFilter}`} onDelete={() => setLoopFolderFilter("")} />}
            {loopTagFilter && <Chip size="small" label={`Tag: ${loopTagFilter}`} onDelete={() => setLoopTagFilter("")} />}
            {loopMediaSearch && <Chip size="small" label={`Suche: ${loopMediaSearch}`} onDelete={() => setLoopMediaSearch("")} />}
            <Chip size="small" label={`${filteredAvailableMedia.length} Medien verfügbar`} />
          </MDBox>

          <MDBox display="flex" gap={1} flexDirection={{ xs: "column", md: "row" }} mb={2}>
            <MDBox flex={1}>
              <MDInput select fullWidth label="Medium auswählen" value={selectedMediaId} onChange={(e) => setSelectedMediaId(e.target.value)}>
                <MenuItem value="" disabled>
                  {filteredAvailableMedia.length === 0 ? "Keine Medien für den aktuellen Filter" : "Bitte auswählen"}
                </MenuItem>
                {filteredAvailableMedia.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {pickMediaLabel(m)}
                    {normalizeFolder(m.folder) ? ` • ${normalizeFolder(m.folder)}` : ""}
                    {` • ${m.mimeType?.startsWith("image/") ? "Bild" : "Video"} • ${formatBytes(m.sizeBytes ?? m.size)}`}
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>
            <MDBox>
              <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={handleAddSelectedMedia} disabled={!selectedMediaId || savingLoop}>
                Hinzufügen
              </MDButton>
            </MDBox>
          </MDBox>

          <Divider sx={{ mb: 2 }} />

          {editedLoop.length === 0 && (
            <MDTypography variant="caption" color="text">
              Dieser Bildschirm hat derzeit noch keinen Medien-Loop. Füge oben Medien hinzu.
            </MDTypography>
          )}

          <MDBox mt={2}>
            {editedLoop.map((item, index) => {
              const m = item.mediaId ? mediaById.get(item.mediaId) : null;
              const type = item.type || inferTypeFromMedia(m);
              const isImage = type === "image";
              const isVideo = type === "video";

              const previewUrl = m?.absoluteUrl || resolveMediaUrl(m?.url || m?.publicUrl || m?.dataUrl);

              return (
                <MDBox
                  key={item.id}
                  mb={2}
                  p={2}
                  borderRadius="xl"
                  border="1px solid #dce3ea"
                  bgcolor="#f9fbfd"
                  display="grid"
                  gridTemplateColumns={{ xs: "1fr", md: "140px 1fr auto" }}
                  gap={2}
                  draggable={!savingLoop && !isCompact}
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                  sx={{ cursor: savingLoop || isCompact ? "default" : "grab", opacity: savingLoop ? 0.6 : 1 }}
                >
                  <MDBox
                    width={{ xs: "100%", md: "140px" }}
                    height={{ xs: "160px", md: "90px" }}
                    borderRadius="lg"
                    overflow="hidden"
                    bgcolor="grey.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {m && isImage && (
                      <img
                        src={previewUrl}
                        alt={pickMediaLabel(m)}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          console.warn("[Screens] Loop image preview failed", { id: m.id, previewUrl });
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    {m && isVideo && (
                      <video
                        src={previewUrl}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        muted
                        playsInline
                        onError={() => {
                          console.warn("[Screens] Loop video preview failed", { id: m.id, previewUrl });
                        }}
                      />
                    )}
                    {!m && (
                      <MDTypography variant="caption" color="text">
                        Medium fehlt
                      </MDTypography>
                    )}
                  </MDBox>

                  <MDBox flex={1} minWidth={0}>
                    <MDBox display="flex" alignItems="center" gap={1} mb={0.2}>
                      <MDBox
                        width="1.6rem"
                        height="1.6rem"
                        borderRadius="md"
                        bgcolor="dark.main"
                        color="white"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="0.75rem"
                      >
                        {index + 1}
                      </MDBox>
                      <MDTypography variant="button" fontWeight="medium" sx={{ display: "block" }}>
                        {m ? pickMediaLabel(m) : "(Nicht zugeordnet)"}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text" display="block">
                      {m ? `${isImage ? "Bild" : "Video"} • ${formatBytes(m.sizeBytes ?? m.size)}` : "Bitte ein Medium auswählen und hinzufügen"}
                    </MDTypography>
                    {m && normalizeFolder(m.folder) && (
                      <MDTypography variant="caption" color="text" display="block">
                        Ordner: {normalizeFolder(m.folder)}
                      </MDTypography>
                    )}
                    {m && normalizeTags(m.tags).length > 0 && (
                      <MDBox mt={0.5} display="flex" gap={0.5} flexWrap="wrap">
                        {normalizeTags(m.tags).slice(0, 4).map((tag) => (
                          <Chip key={`${m.id}-${tag}`} size="small" label={tag} />
                        ))}
                      </MDBox>
                    )}

                    {m && isImage && (
                      <MDBox mt={1} width={{ xs: "100%", md: "10rem" }}>
                        <MDInput
                          fullWidth
                          type="number"
                          label="Dauer (Sek.)"
                          value={item.duration ?? 10}
                          onChange={(e) => handleChangeDuration(index, e.target.value)}
                          disabled={savingLoop}
                        />
                      </MDBox>
                    )}

                    {m && isVideo && (
                      <MDTypography variant="caption" color="text" display="block" mt={1}>
                        Video: läuft vollständig (Dauer wird automatisch übernommen).
                      </MDTypography>
                    )}
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      color="dark"
                      onClick={() => handleMoveItem(index, -1)}
                      disabled={savingLoop || index === 0}
                    >
                      <Icon>keyboard_arrow_up</Icon>
                    </IconButton>
                    <IconButton
                      size="small"
                      color="dark"
                      onClick={() => handleMoveItem(index, 1)}
                      disabled={savingLoop || index === editedLoop.length - 1}
                    >
                      <Icon>keyboard_arrow_down</Icon>
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteItem(index)} disabled={savingLoop}>
                      <Icon>delete</Icon>
                    </IconButton>
                    <MDBox display={{ xs: "none", md: "flex" }} alignItems="center" title="Drag & Drop">
                      <Icon fontSize="small">drag_indicator</Icon>
                    </MDBox>
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <MDButton variant="text" color="secondary" onClick={handleCloseLoopEditor} disabled={savingLoop}>
            Abbrechen
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleSaveLoop} disabled={savingLoop}>
            {savingLoop ? "Speichere..." : "Loop speichern"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
